import type { TFunction } from "i18next";
import {
  LinkRewardTypeType,
  LinkTypeType,
  type CreateLinkInputType,
  type LinkRewardInputType,
} from "@/types/graphql/partner.generated";
import type { LinkFormValues, LinkRewardFormType } from "./types";
import { linkTargetInput } from "./linkTargetValues";

/** Подписи переключателя «Награда» (явная мапа ключей, CRITICAL-RULES i18n п. 3). */
export const LINK_REWARD_TYPE_LABEL_KEYS: Record<LinkRewardFormType, string> = {
  NONE: "links.form.rewardNone",
  PERCENT: "links.form.rewardPercent",
  FIXED: "links.form.rewardFixed",
};

type LinkRewardFields = Pick<
  LinkFormValues,
  "rewardType" | "rewardPercent" | "rewardFixedAmount"
>;

interface LinkRewardLike {
  type: LinkRewardTypeType;
  percent?: number | null;
  fixedAmount?: number | null;
}

/** Награда ссылки в поля формы: тип и одно значение. */
export function rewardToFormValues(
  reward: LinkRewardLike | null | undefined
): LinkRewardFields {
  return {
    rewardType: reward?.type ?? "NONE",
    rewardPercent: reward?.percent ?? null,
    rewardFixedAmount: reward?.fixedAmount ?? null,
  };
}

function rewardInput(values: LinkFormValues): LinkRewardInputType | null {
  if (values.rewardType === LinkRewardTypeType.PercentType) {
    return {
      type: LinkRewardTypeType.PercentType,
      percent: values.rewardPercent,
    };
  }
  if (values.rewardType === LinkRewardTypeType.FixedType) {
    return {
      type: LinkRewardTypeType.FixedType,
      fixedAmount: values.rewardFixedAmount,
    };
  }
  return null;
}

/**
 * Значения формы во вход createLink/updateLink: поля переключателя награды
 * в API не уходят, вместо них reward (null снимает награду).
 */
export function linkFormValuesToInput(
  values: LinkFormValues
): CreateLinkInputType {
  const internal = values.linkType === LinkTypeType.InternalType;
  return {
    title: values.title,
    slug: values.slug,
    linkType: values.linkType,
    href: internal ? null : values.href,
    ...linkTargetInput(values, internal),
    utmSource: values.utmSource,
    utmMedium: values.utmMedium,
    utmCampaign: values.utmCampaign,
    reward: rewardInput(values),
  };
}

const hasCents = (value: number) =>
  Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;

/**
 * Проверка блока «Награда» до отправки, те же правила, что LinkValidator на
 * бэкенде: значение обязательно, процент 0..100, сумма не меньше 0, до сотых.
 */
export function linkRewardFormErrors(
  values: Partial<LinkFormValues>,
  t: TFunction
): Record<string, string> {
  if (values.rewardType === LinkRewardTypeType.PercentType) {
    const percent = values.rewardPercent;
    if (percent == null || Number.isNaN(percent)) {
      return { rewardPercent: t("links.form.rewardRequired") };
    }
    if (percent < 0 || percent > 100) {
      return { rewardPercent: t("links.form.rewardPercentRange") };
    }
    if (!hasCents(percent)) {
      return { rewardPercent: t("links.form.rewardPrecision") };
    }
  }
  if (values.rewardType === LinkRewardTypeType.FixedType) {
    const amount = values.rewardFixedAmount;
    if (amount == null || Number.isNaN(amount)) {
      return { rewardFixedAmount: t("links.form.rewardRequired") };
    }
    if (amount < 0) {
      return { rewardFixedAmount: t("links.form.rewardAmountMin") };
    }
    if (!hasCents(amount)) {
      return { rewardFixedAmount: t("links.form.rewardPrecision") };
    }
  }
  return {};
}
